import React, { useEffect, useRef, useState } from 'react';
import { mdiChevronDown, mdiChevronUp, mdiCreationOutline } from '@mdi/js';
import Icon from '@mdi/react';
import {
    ProviderProfessionalClaimTemplate,
    SupportProfessionalClaimTemplate,
} from '../../../api';
import { buildAiConversationId } from '../../../services/ai-adapter/protocol';
import { useAi } from '../../../services/ai-adapter/hook';
import { useAiExtensionConnectionStatus } from '../../../services/ai-adapter/connection-check';
import {
    ClaimContextSources,
    buildClaimSmartContext,
    stringifyContextForAi,
} from '../../../services/ai-adapter/smart-context';
import { buildUtilityFromKey } from '../../../services/ai-adapter/utility/utility';
import { formatDistanceToNow } from '../../../services/primitives/date-utils';
import { Spinner } from '../../base';
import { CLAIM_AI_SUMMARY_PROMPT } from './ai-summary.prompt';
import { buildServiceLineMatchSummary } from './build-service-line-match-summary';
import { parseAiSummaryResponse } from './parse-ai-summary';
import * as S from './ai-summary-card.styles';

export interface IClaimAiSummaryCardProps {
    /** Claim context sources — same shape used by Talk to Claim. */
    sources: ClaimContextSources;
    /** Utility key, e.g. "claimDetails:Provider" or "claimDetails:Support". */
    utilityKey: string;
    /** Opens the Talk to Claim panel (blank). */
    onAskFollowUp: () => void;
    /**
     * When false, the card stays hidden even if the extension is connected
     * (e.g. claim detail data still loading).
     */
    ready?: boolean;
    /** Re-trigger generation when this identity changes (typically claim id). */
    claimId?: string;
    /**
     * Professional claim template used to derive service-line ↔ payment matching
     * for the AI context. Ignored for institutional / NYNF3 claims.
     */
    professionalTemplate?: ProviderProfessionalClaimTemplate | SupportProfessionalClaimTemplate;
}

export const ClaimAiSummaryCard: React.FC<IClaimAiSummaryCardProps> = ({
    sources,
    utilityKey,
    onAskFollowUp,
    ready = true,
    claimId,
    professionalTemplate,
}) => {
    const { connected } = useAiExtensionConnectionStatus({ autoCheck: true });
    const { askAi, loading, error } = useAi();

    const [collapsed, setCollapsed] = useState(false);
    const [summary, setSummary] = useState<string>('');
    const [nextStep, setNextStep] = useState<string | null>(null);
    const [generatedAt, setGeneratedAt] = useState<Date | null>(null);
    const [tick, setTick] = useState(0);
    const [requestError, setRequestError] = useState<string | null>(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const requestIdRef = useRef(0);
    const sourcesRef = useRef(sources);
    sourcesRef.current = sources;
    const templateRef = useRef(professionalTemplate);
    templateRef.current = professionalTemplate;

    const claimIdentity = claimId ?? String((sources.claim as { id?: string } | undefined)?.id ?? '');
    const hasProfessionalTemplate = !!professionalTemplate?.f24ServiceLine;

    useEffect(() => {
        if (connected !== true || !ready) {
            return;
        }

        const requestId = ++requestIdRef.current;
        setRequestError(null);
        setSummary('');
        setNextStep(null);
        setGeneratedAt(null);
        setIsGenerating(true);

        const run = async () => {
            try {
                const currentSources = sourcesRef.current;
                const { context } = buildClaimSmartContext({
                    question: CLAIM_AI_SUMMARY_PROMPT,
                    sources: currentSources,
                    attachedItems: [],
                    previouslyMatchedKeys: [],
                });

                const enriched: Record<string, unknown> =
                    context && typeof context === 'object'
                        ? { ...(context as Record<string, unknown>) }
                        : {};
                if (currentSources.payments?.length) {
                    enriched.payments = currentSources.payments;
                }
                if (currentSources.collectionPayments?.length) {
                    enriched.collectionPayments = currentSources.collectionPayments;
                }

                const serviceLineMatching = buildServiceLineMatchSummary(
                    templateRef.current,
                    currentSources.payments as Parameters<typeof buildServiceLineMatchSummary>[1],
                );
                if (serviceLineMatching) {
                    enriched.serviceLineMatching = serviceLineMatching;
                }

                const utility = buildUtilityFromKey(utilityKey);
                const dataJson = stringifyContextForAi(enriched);
                const result = await askAi(
                    buildAiConversationId('claimSummary', claimIdentity || 'unknown'),
                    'summarize',
                    CLAIM_AI_SUMMARY_PROMPT,
                    {
                        entity: { name: 'Claim' },
                        dataJson,
                    },
                    utility,
                );

                if (requestId !== requestIdRef.current) {
                    return;
                }

                const parsed = parseAiSummaryResponse(result.assistantMessage.content);
                setSummary(parsed.summary);
                setNextStep(parsed.nextStep);
                setGeneratedAt(new Date());
            } catch (e: unknown) {
                if (requestId !== requestIdRef.current) {
                    return;
                }
                setRequestError(typeof e === 'string' ? e : e instanceof Error ? e.message : 'AI error');
            } finally {
                if (requestId === requestIdRef.current) {
                    setIsGenerating(false);
                }
            }
        };

        void run();
        // Re-generate when claim identity / readiness / connection / template availability change —
        // not on every parent re-render that creates a new sources object.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [claimIdentity, ready, connected, utilityKey, hasProfessionalTemplate]);

    // Keep "Updated n minutes ago" fresh while the page is open.
    useEffect(() => {
        if (!generatedAt) {
            return;
        }
        const id = window.setInterval(() => setTick(t => t + 1), 60_000);
        return () => window.clearInterval(id);
    }, [generatedAt]);

    if (connected !== true || !ready) {
        return null;
    }

    const busy = isGenerating || loading;
    const updatedLabel =
        generatedAt != null
            ? `Updated ${formatDistanceToNow(generatedAt)} ago`
            : busy
              ? 'Generating…'
              : null;

    // Reference tick so relative label re-renders on the interval.
    void tick;

    return (
        <S.Card>
            <S.Header>
                <S.HeaderLeft>
                    <S.IconBadge>
                        <S.TitleIcon path={mdiCreationOutline} size={0.6} />
                    </S.IconBadge>
                    <S.Title>AI Summary</S.Title>
                    {updatedLabel && <S.UpdatedAt>{updatedLabel}</S.UpdatedAt>}
                </S.HeaderLeft>
                <S.HeaderRight>
                    <S.FollowUpButton
                        variant="text"
                        type="button"
                        onClick={onAskFollowUp}
                        disabled={busy}>
                        <S.FollowUpIcon path={mdiCreationOutline} size={0.55} />
                        Ask a follow-up
                    </S.FollowUpButton>
                    <S.CollapseButton
                        type="button"
                        aria-label={collapsed ? 'Expand AI Summary' : 'Collapse AI Summary'}
                        aria-expanded={!collapsed}
                        onClick={() => setCollapsed(c => !c)}>
                        <Icon path={collapsed ? mdiChevronDown : mdiChevronUp} size={0.7} />
                    </S.CollapseButton>
                </S.HeaderRight>
            </S.Header>

            {!collapsed && (
                <>
                    {busy && (
                        <S.LoadingRow>
                            <Spinner size="16px" />
                            Generating claim summary…
                        </S.LoadingRow>
                    )}

                    {!busy && (requestError || error) && (
                        <S.ErrorText>{requestError || error}</S.ErrorText>
                    )}

                    {!busy && !requestError && !error && summary && (
                        <S.Body>
                            <S.SummaryText text={summary} />
                            {nextStep && (
                                <S.NextRow>
                                    <S.NextLabel>Next</S.NextLabel>
                                    <S.SummaryText text={nextStep} />
                                </S.NextRow>
                            )}
                        </S.Body>
                    )}
                </>
            )}
        </S.Card>
    );
};
